/**
 * Where the identity provider sends the person back after a signature's re-authentication (§5.6; ESIG-001,
 * ESIG-034; 21 CFR 11.200(a)). It exchanges the code for the fresh id token, applies the signature with it
 * under the session the person already holds, records the outcome for the screen they return to, and puts
 * the address back where they were -- they do not lose their place.
 *
 * The session token is deliberately left alone: if someone else re-authenticated, the server refuses the
 * signature (SIGNER_MISMATCH) and this person stays signed in as themselves, with nothing signed.
 */
import { useEffect } from "react";
import { ApiError, api, endpoints } from "../lib/api.js";
import { completeSignIn } from "../lib/auth.js";
import { alarmMgmt } from "../lib/control-alarm-api.js";
import { ACTIVATION_REASON_KEY, controlEng } from "../lib/control-eng-api.js";
import {
  type SigningIntent,
  saveOutcome,
  signingRefusal,
  stepUpErrorMessage,
  takeIntent,
} from "../lib/signing.js";

const BASE = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");

/** Runs once per page load even under StrictMode's double effect: the intent is single-use. */
let inFlight: Promise<string> | null = null;

async function apply(intent: SigningIntent, idToken: string): Promise<void> {
  // Process Control subjects are signed through their own routes, which carry out what was signed (docs/control/API.md).
  if (intent.subjectType === "control_release")
    return void (await controlEng.approve(intent.subjectId, { payloadHash: intent.payloadHash, idToken }));
  if (intent.subjectType === "control_release_activation") {
    const reason = sessionStorage.getItem(ACTIVATION_REASON_KEY) ?? "";
    sessionStorage.removeItem(ACTIVATION_REASON_KEY);
    return void (await controlEng.activate(intent.subjectId, {
      reason,
      payloadHash: intent.payloadHash,
      idToken,
    }));
  }
  if (intent.subjectType === "control_request")
    return void (await controlEng.signRequest(intent.subjectId, {
      payloadHash: intent.payloadHash,
      idToken,
    }));
  if (intent.subjectType === "control_alarm_request")
    return void (await alarmMgmt.signShelve(intent.subjectId, { payloadHash: intent.payloadHash, idToken }));
  await endpoints.applySignature(intent.batchId, {
    subjectType: intent.subjectType,
    subjectId: intent.subjectId,
    meaning: intent.meaning,
    payloadHash: intent.payloadHash,
    idToken,
  });
}

async function finish(): Promise<string> {
  const intent = takeIntent(sessionStorage);
  const fail = (message: string, subjectId = "", meaning = "") =>
    saveOutcome(sessionStorage, { ok: false, subjectId, meaning, message });
  try {
    const token = await completeSignIn(); // always consumes the code and clears it from the address bar
    if (!intent || !token) {
      fail("That signature request expired or was already used. Nothing was signed. Press Sign again.");
      return intent?.returnPath ?? "/";
    }
    await apply(intent, token.idToken);
    saveOutcome(sessionStorage, {
      ok: true,
      subjectId: intent.subjectId,
      meaning: intent.meaning,
      message: `Signed as ${intent.meaning}.`,
    });
  } catch (e) {
    const message =
      e instanceof ApiError
        ? signingRefusal(e.failure.code, e.failure.message)
        : stepUpErrorMessage(e instanceof Error ? e.message : "");
    fail(message, intent?.subjectId, intent?.meaning);
  }
  return intent?.returnPath ?? "/";
}

export function SigningReturn({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    inFlight ??= finish();
    void inFlight.then((path) => {
      inFlight = null;
      window.history.replaceState({}, "", `${BASE}${path}`);
      onDone();
    });
  }, [onDone]);

  return (
    <main style={{ padding: "var(--space-6)" }}>
      <p role="status" style={{ fontSize: "var(--type-body-lg-floor-size)" }}>
        Applying your signature…
      </p>
    </main>
  );
}
